import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Eye, TrendingUp, Clock, CheckCircle, AlertCircle, DollarSign, Euro, PlusCircle, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

const EvenementsList = () => {
  const [evenements, setEvenements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statutFilter, setStatutFilter] = useState('all');
  const [financialFilter, setFinancialFilter] = useState('all');
  const [clientType, setClientType] = useState('all');
  const [selectedClient, setSelectedClient] = useState('all');
  const [clients, setClients] = useState([]);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newEvenementData, setNewEvenementData] = useState({
    client_id: '',
    event_title: '',
    event_date: '',
    total_ttc: ''
  });
  const [stats, setStats] = useState({
    ca_reel: 0,
    total_en_attente: 0,
    total_solde: 0,
    total_en_poche: 0,
    ca_percu_annee: 0,
    annee_civile: new Date().getFullYear(),
  });

  const buildFilterParams = () => {
    const params = new URLSearchParams();
    if (statutFilter !== 'all') params.append('statut', statutFilter);
    if (financialFilter !== 'all') params.append('statut_financier', financialFilter);
    if (clientType !== 'all') params.append('client_type', clientType);
    if (selectedClient !== 'all') params.append('client_id', selectedClient);
    if (dateStart) params.append('date_start', dateStart);
    if (dateEnd) params.append('date_end', dateEnd);
    if (searchTerm) params.append('search', searchTerm);
    return params;
  };

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      loadEvenements();
      loadStats();
    }, 300);
    return () => clearTimeout(handler);
  }, [statutFilter, financialFilter, clientType, selectedClient, dateStart, dateEnd, searchTerm]);

  const loadClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des clients');
    }
  };

  const loadStats = async () => {
    try {
      const params = buildFilterParams();
      const res = await axios.get(`${API}/evenements/stats`, { params, withCredentials: true });
      setStats({
        ca_reel: parseFloat(res.data.ca_reel) || 0,
        total_en_attente: parseFloat(res.data.total_en_attente) || 0,
        total_solde: parseInt(res.data.total_solde, 10) || 0,
        total_en_poche: parseFloat(res.data.total_en_poche) || 0,
        ca_percu_annee: parseFloat(res.data.ca_percu_annee) || 0,
        annee_civile: parseInt(res.data.annee_civile, 10) || new Date().getFullYear(),
      });
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    }
  };

  const loadEvenements = async () => {
    setLoading(true);
    try {
      const params = buildFilterParams();
      const res = await axios.get(`${API}/evenements`, { params, withCredentials: true });
      setEvenements(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des événements');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvenement = async () => {
    try {
      const res = await axios.post(`${API}/evenements`, newEvenementData, { withCredentials: true });
      toast.success('Entrée créée avec succès');
      setIsCreateModalOpen(false);
      setNewEvenementData({ client_id: '', event_title: '', event_date: '', total_ttc: '' });
      loadEvenements(); // Reload to get the new event and respect sorting
      loadStats();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleDeleteEvenement = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette entrée ? Cette action est irréversible.')) {
      try {
        await axios.delete(`${API}/evenements/${id}`, { withCredentials: true });
        toast.success('Entrée supprimée avec succès');
        loadEvenements(); // Easiest way to refresh the list and stats
        loadStats();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const getStatutBadge = (statut) => {
    const config = {
      en_preparation: { label: '📋 Préparation', color: 'bg-blue-100 text-blue-700' },
      en_cours: { label: '🎬 En cours', color: 'bg-amber-100 text-amber-700' },
      termine: { label: '✅ Terminé', color: 'bg-green-100 text-green-700' }
    };
    const c = config[statut] || config.en_preparation;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getFinancialBadge = (statut) => {
    const config = {
      en_attente: { label: '⏳ En attente', color: 'bg-stone-100 text-stone-700' },
      acompte_recu: { label: '💰 Acompte reçu', color: 'bg-blue-100 text-blue-700' },
      partiellement_paye: { label: '📊 Partiel', color: 'bg-amber-100 text-amber-700' },
      solde: { label: '✅ Soldé', color: 'bg-green-100 text-green-700' }
    };
    const c = config[statut] || config.en_attente;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6 sm:mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-stone-800">Finances</h1>
          <p className="text-sm sm:text-base text-stone-600">Suivi financier et gestion des événements</p>
        </div>
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogTrigger asChild>
            <Button>
              <PlusCircle className="h-4 w-4 mr-2" />
              Ajouter une entrée
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer une entrée financière manuelle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Client</Label>
                <Select onValueChange={(value) => setNewEvenementData(prev => ({ ...prev, client_id: value }))}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner un client..." /></SelectTrigger>
                  <SelectContent>
                    {clients.map(client => <SelectItem key={client.id} value={client.id}>{client.prenom} {client.nom}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Titre de l'événement</Label>
                <Input value={newEvenementData.event_title} onChange={(e) => setNewEvenementData(prev => ({ ...prev, event_title: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de l'événement</Label>
                  <Input type="date" value={newEvenementData.event_date} onChange={(e) => setNewEvenementData(prev => ({ ...prev, event_date: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Total TTC (€)</Label>
                  <Input type="number" value={newEvenementData.total_ttc} onChange={(e) => setNewEvenementData(prev => ({ ...prev, total_ttc: e.target.value }))} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setIsCreateModalOpen(false)}>Annuler</Button>
              <Button onClick={handleCreateEvenement}>Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-stone-600 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              CA perçu {stats.annee_civile}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-emerald-600">{stats.ca_percu_annee.toLocaleString('fr-FR')}€</p>
            <p className="text-xs text-stone-500 mt-1">Paiements reçus (année civile)</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-stone-600 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              CA Réel (Reçu)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-600">{stats.ca_reel.toLocaleString('fr-FR')}€</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-stone-600 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              En Attente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-amber-600">{stats.total_en_attente.toLocaleString('fr-FR')}€</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-stone-600 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Soldés
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-blue-600">{stats.total_solde}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-stone-600 flex items-center gap-2">
              <Euro className="h-4 w-4" />
              En Poche (Net)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-stone-800">{stats.total_en_poche.toLocaleString('fr-FR')}€</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <Input
              placeholder="Rechercher par client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="lg:col-span-2"
            />
            <Select value={clientType} onValueChange={setClientType}>
              <SelectTrigger><SelectValue placeholder="Type de client" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="particulier">Particulier</SelectItem>
                <SelectItem value="partenaire">Partenaire</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger><SelectValue placeholder="Client" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les clients</SelectItem>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id.toString()}>
                    {client.prenom} {client.nom}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={financialFilter} onValueChange={setFinancialFilter}>
              <SelectTrigger><SelectValue placeholder="Statut financier" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="en_attente">⏳ En attente</SelectItem>
                <SelectItem value="acompte_recu">💰 Acompte reçu</SelectItem>
                <SelectItem value="partiellement_paye">📊 Partiel</SelectItem>
                <SelectItem value="solde">✅ Soldé</SelectItem>
              </SelectContent>
            </Select>
            <div className="lg:col-start-2 lg:col-span-2 flex items-center gap-2">
              <Input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} />
              <span className="text-stone-500">à</span>
              <Input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table - Desktop */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-stone-700">Client</TableHead>
                  <TableHead className="text-stone-700">Événement</TableHead>
                  <TableHead className="text-stone-700">Date</TableHead>
                  <TableHead className="text-stone-700">Total TTC</TableHead>
                  <TableHead className="text-stone-700">Reçu</TableHead>
                  <TableHead className="text-stone-700">Reste</TableHead>
                  <TableHead className="text-stone-700">Statut</TableHead>
                  <TableHead className="text-stone-700">Financier</TableHead>
                  <TableHead className="text-right text-stone-700">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8">Chargement...</TableCell></TableRow>
                ) : evenements.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-stone-500">Aucun événement trouvé.</TableCell></TableRow>
                ) : (
                  evenements.map((evt) => (
                    <TableRow key={evt.id}>
                      <TableCell className="font-medium">
                        {evt.client_info?.prenom} {evt.client_info?.nom}
                      </TableCell>
                      <TableCell>
                        <div className="capitalize">
                          {evt.event_info?.type || evt.event_info?.event_type?.replace('_', ' ')}
                        </div>
                        <div className="text-xs text-stone-500">
                          {evt.event_info?.title || evt.event_info?.vedettes}
                        </div>
                        {evt.devis_id?.startsWith('NOTE-') && (
                          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200 mt-1">
                            Note d'Auteur
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(evt.event_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="font-semibold">{evt.total_ttc?.toLocaleString()}€</TableCell>
                      <TableCell className="text-green-600 font-semibold">{evt.total_recu?.toLocaleString()}€</TableCell>
                      <TableCell className="text-amber-600 font-semibold">{evt.reste_a_payer?.toLocaleString()}€</TableCell>
                      <TableCell>{getStatutBadge(evt.statut)}</TableCell>
                      <TableCell>{getFinancialBadge(evt.statut_financier)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/backoffice/finances/${evt.id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteEvenement(evt.id)} className="text-red-500 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Cards - Mobile */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <Card><CardContent className="p-4 text-center">Chargement...</CardContent></Card>
        ) : evenements.length === 0 ? (
          <Card><CardContent className="p-4 text-center text-stone-500 text-sm">Aucun événement trouvé.</CardContent></Card>
        ) : (
          evenements.map((evt) => (
            <Card key={evt.id}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="font-semibold text-stone-800">
                      {evt.client_info?.prenom} {evt.client_info?.nom}
                    </div>
                    <div className="text-sm capitalize text-stone-600">
                      {evt.event_info?.event_type?.replace('_', ' ')}
                    </div>
                    <div className="text-xs text-stone-500">
                      {new Date(evt.event_date).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-stone-500">{evt.event_info?.vedettes}</div>
                  </div>
                  <div className="flex">
                    <Button variant="ghost" size="sm" asChild>
                      <Link to={`/backoffice/finances/${evt.id}`}>
                        <Eye className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteEvenement(evt.id)} className="text-red-500 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div>
                    <div className="text-xs text-stone-500">Total TTC</div>
                    <div className="font-semibold">{evt.total_ttc?.toLocaleString()}€</div>
                  </div>
                  <div>
                    <div className="text-xs text-stone-500">Reçu</div>
                    <div className="font-semibold text-green-600">{evt.total_recu?.toLocaleString()}€</div>
                  </div>
                  <div>
                    <div className="text-xs text-stone-500">Reste</div>
                    <div className="font-semibold text-amber-600">{evt.reste_a_payer?.toLocaleString()}€</div>
                  </div>
                  <div>
                    <div className="text-xs text-stone-500">En poche</div>
                    <div className="font-semibold text-blue-600">{evt.reste_en_poche?.toLocaleString()}€</div>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {getStatutBadge(evt.statut)}
                  {getFinancialBadge(evt.statut_financier)}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default EvenementsList;